import { LayerExtension } from "@deck.gl/core"
import { _TerrainExtension as TerrainExtension } from "@deck.gl/extensions"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import EventEmitter from "events"
import { pick } from "nodash"
import { TerrainSpecification } from "maplibre-gl"
import { useState } from "react"

const globalTerrainStyle: TerrainSpecification = {
    source: "terrain",
    exaggeration: 1,
}

const globalTerrainSettings: {
    terrainState: boolean
    extensions: LayerExtension[]
    terrainStyle: TerrainSpecification | undefined
    isDisabled: boolean
    terrainMapIsLoaded: boolean
} = {
    terrainState: false,
    extensions: [],
    terrainStyle: undefined,
    isDisabled: false,
    terrainMapIsLoaded: false,
}

export const terrainStateEmitter = new EventEmitter()

export const getGlobalTerrainData = () => globalTerrainSettings

export const setGlobalTerrainData = (
    settings: Omit<Partial<typeof globalTerrainSettings>, "terrainStyle" | "extensions">
) => {
    globalTerrainSettings.terrainMapIsLoaded =
        settings.terrainMapIsLoaded ?? globalTerrainSettings.terrainMapIsLoaded

    globalTerrainSettings.isDisabled = settings.isDisabled ?? globalTerrainSettings.isDisabled

    if (
        "terrainState" in settings &&
        globalTerrainSettings.terrainState != !!settings.terrainState
    ) {
        globalTerrainSettings.terrainState = !!settings.terrainState
        globalTerrainSettings.extensions = globalTerrainSettings.terrainState
            ? [new TerrainExtension()]
            : []

        globalTerrainSettings.terrainStyle = globalTerrainSettings.terrainState
            ? globalTerrainStyle
            : undefined
    }

    terrainStateEmitter.emit("updatedTerrainState")
}

export const useGlobalTerrainData = <T extends (keyof typeof globalTerrainSettings)[]>(
    subscriptions: T
) => {
    const [terrainData, setTerrainDataState] = useState<
        Pick<typeof globalTerrainSettings, T[number]>
    >(pick(globalTerrainSettings, subscriptions))

    useEffectUnsafe(() => {
        const setNewTerrainData = () => {
            setTerrainDataState((prevData) => {
                const hasChanged = subscriptions.some((key) => {
                    return prevData[key as T[number]] != globalTerrainSettings[key]
                })

                return hasChanged ? pick(globalTerrainSettings, subscriptions) : prevData
            })
        }

        terrainStateEmitter.on("updatedTerrainState", setNewTerrainData)
        return () => {
            terrainStateEmitter.off("updatedTerrainState", setNewTerrainData)
        }
    }, subscriptions)

    return terrainData
}
